import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Upload, Button, Space, Tag, message, Popconfirm, Alert, Row, Col, Statistic, Modal, DatePicker, Input, Dropdown } from 'antd';
import { UploadOutlined, ReloadOutlined, CheckOutlined, UndoOutlined, FileExcelOutlined, SaveOutlined, DeleteOutlined, DownOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiFetch } from '../utils/api.js';
import { yuan } from '../utils/format.js';

// 会话级缓存：切换页面后回到本页，上传的账单解析结果不丢失（关浏览器标签页自动清空）
const PREVIEW_KEY = 'freightBill.preview';
const SUMMARY_KEY = 'freightBill.appliedSummary';
function loadSession(key) {
  try { const s = sessionStorage.getItem(key); return s ? JSON.parse(s) : null; } catch (e) { return null; }
}

export default function FreightBill() {
  const [preview, setPreview] = useState(() => loadSession(PREVIEW_KEY)); // 解析预览
  const [previewKey, setPreviewKey] = useState(0);
  const [history, setHistory] = useState([]);
  const [applying, setApplying] = useState(false);
  const [historyModal, setHistoryModal] = useState(null); // 查看历史明细
  const [monthly, setMonthly] = useState([]); // 月度对账记录（手动归档留底）
  const [monthlySummary, setMonthlySummary] = useState([]); // 按月自动汇总（合并当月全部对账执行记录）
  const [appliedSummary, setAppliedSummary] = useState(() => loadSession(SUMMARY_KEY)); // 确认对账后待保存的汇总
  const [saveModal, setSaveModal] = useState(false);
  const [saveMonth, setSaveMonth] = useState(dayjs().format('YYYY-MM'));
  const [saveRemark, setSaveRemark] = useState('');
  const [saving, setSaving] = useState(false);
  const [regionModal, setRegionModal] = useState(false); // 修正区域弹窗
  const [regionSel, setRegionSel] = useState([]); // 勾选的 order_id
  const [regionUpdating, setRegionUpdating] = useState(false);

  const loadHistory = useCallback(() => {
    apiFetch('/api/freight-bill/history').then(r => r.json()).then(d => setHistory(d.data || [])).catch(() => {});
  }, []);
  const loadMonthly = useCallback(() => {
    apiFetch('/api/freight-bill/monthly').then(r => r.json()).then(d => {
      setMonthly(d.data || []);
      setMonthlySummary(d.summary || []);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadHistory(); loadMonthly(); }, [loadHistory, loadMonthly]);

  // 解析结果 / 已确认对账状态 → 写入 sessionStorage，切页回来自动恢复
  useEffect(() => {
    try { preview ? sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(preview)) : sessionStorage.removeItem(PREVIEW_KEY); } catch (e) {}
  }, [preview]);
  useEffect(() => {
    try { appliedSummary ? sessionStorage.setItem(SUMMARY_KEY, JSON.stringify(appliedSummary)) : sessionStorage.removeItem(SUMMARY_KEY); } catch (e) {}
  }, [appliedSummary]);

  const parseBill = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await apiFetch('/api/freight-bill/parse', { method: 'POST', body: fd }).then(r => r.json());
    if (res.success) { setPreview(res.preview); setPreviewKey(k => k + 1); message.success('账单解析成功'); }
    else message.error(res.error || '解析失败');
    return false;
  };

  const applyBill = async () => {
    setApplying(true);
    const res = await apiFetch('/api/freight-bill/apply', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ preview, company: '极兔快递', bill_date: new Date().toISOString().slice(0, 10) }),
    }).then(r => r.json());
    setApplying(false);
    if (res.success) {
      message.success(`对账完成：更新 ${res.recon.applied} 笔订单运费并重算利润`);
      setAppliedSummary({ summary: preview, recon_ids: [res.recon.id] });
      loadHistory();
    }
    else message.error(res.error || '应用失败');
  };

  // 保存本月对账记录（归档留底）
  const saveRecord = async () => {
    if (!appliedSummary) return;
    setSaving(true);
    const res = await apiFetch('/api/freight-bill/save-monthly', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        month: saveMonth, remark: saveRemark,
        summary: appliedSummary.summary, recon_ids: appliedSummary.recon_ids,
      }),
    }).then(r => r.json());
    setSaving(false);
    if (res.success) {
      message.success('本月对账记录已保存');
      setSaveModal(false);
      setAppliedSummary(null);
      setPreview(null);
      setSaveRemark('');
      setSaveMonth(dayjs().format('YYYY-MM'));
      loadMonthly();
    }
    else message.error(res.error || '保存失败');
  };

  const deleteMonthly = async (id) => {
    const res = await apiFetch(`/api/freight-bill/monthly/${id}`, { method: 'DELETE' }).then(r => r.json());
    if (res.success) { message.success('已删除'); loadMonthly(); }
    else message.error(res.error || '删除失败');
  };

  const revert = async (reconId, orderId) => {
    const res = await apiFetch('/api/freight-bill/revert', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ recon_id: reconId, order_id: orderId }),
    }).then(r => r.json());
    if (res.success) { message.success('已撤销，运费已恢复'); loadHistory(); }
    else message.error(res.error || '撤销失败');
  };

  // 打开修正区域弹窗：默认勾选所有「可自动映射」的区域不一致订单
  const openRegionModal = () => {
    const mismatch = (preview.matched || []).filter(m => m.region_mismatch);
    setRegionSel(mismatch.filter(m => m.mappable).map(m => m.platform_order_no || String(m.order_id)));
    setRegionModal(true);
  };

  // 一键修正区域：把勾选订单的系统区域改为账单区域
  const updateRegions = async () => {
    if (!regionSel.length) { message.warning('请至少勾选一单'); return; }
    setRegionUpdating(true);
    const res = await apiFetch('/api/freight-bill/update-regions', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ preview, order_keys: regionSel }),
    }).then(r => r.json());
    setRegionUpdating(false);
    if (res.success) {
      message.success(`已修正 ${res.changed} 单区域`);
      const toMap = {};
      (res.results || []).forEach(r => { if (r.ok) toMap[r.platform_order_no || String(r.order_id)] = r.to; });
      const newMatched = preview.matched.map(m => {
        const key = m.platform_order_no || String(m.order_id);
        return toMap[key] ? { ...m, sys_region: toMap[key], region_mismatch: false } : m;
      });
      const newMismatch = newMatched.filter(m => m.region_mismatch).length;
      setPreview({ ...preview, matched: newMatched, mismatch_count: newMismatch });
      setRegionModal(false);
      setRegionSel([]);
    }
    else message.error(res.error || '修正失败');
  };

  // 导出差异明细（可按分类分开导出：未匹配/运费差额/区域不一致/三合一）
  const EXPORT_TYPES = [
    { key: 'unmatched', label: '未匹配单' },
    { key: 'diff', label: '运费差额单' },
    { key: 'region', label: '区域不一致单' },
    { key: 'all', label: '全部（三合一）' },
  ];
  const exportDiff = async (type = 'all') => {
    try {
      const res = await apiFetch('/api/freight-bill/export', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preview, type }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || '导出失败');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const typeName = { unmatched: '未匹配单', diff: '运费差额单', region: '区域不一致单', all: '快递对账差异明细' }[type] || '快递对账差异明细';
      a.download = `${typeName}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      message.success(`${typeName}已导出`);
    } catch (e) { message.error(e.message || '导出失败'); }
  };

  const matchedColumns = [
    { title: '快递单号', dataIndex: 'tracking_no', width: 150 },
    { title: '店铺', dataIndex: 'shop', width: 130, ellipsis: true },
    { title: '商品', dataIndex: 'product_name', ellipsis: true },
    { title: '系统原运费', dataIndex: 'old_fee', width: 100, align: 'right', render: v => yuan(v) },
    { title: '账单运费', dataIndex: 'new_fee', width: 100, align: 'right', render: v => <b style={{ color: 'var(--brand)' }}>{yuan(v)}</b> },
    { title: '差额', key: 'diff', width: 100, align: 'right',
      render: (_, r) => { const d = r.new_fee - r.old_fee; return <span style={{ color: d > 0 ? '#CF1322' : d < 0 ? '#3F8600' : '#999', fontWeight: 600 }}>{d > 0 ? '+' : ''}{yuan(d)}</span>; } },
    { title: '区域校验', key: 'region', width: 120, align: 'center',
      render: (_, r) => r.region_mismatch
        ? <Tag color="#CF1322">账单{r.bill_region}≠系统{r.sys_region}</Tag>
        : (r.bill_region ? <Tag color="#3F8600">一致({r.bill_region})</Tag> : <span style={{ color: '#bfbfbf' }}>—</span>) },
    { title: '多包裹', key: 'multi_tracking', width: 130, align: 'center',
      render: (_, r) => r.multi_tracking ? <Tag color="#2F54EB">多包裹·{r.track_count}个单号加总</Tag> : '' },
    { title: '退件', dataIndex: 'is_return', width: 60, align: 'center', render: v => v ? <Tag color="#EF9F27">退件</Tag> : '' },
  ];

  return (
    <div className="page-container">
      <Card title="快递对账（上传快递账单 → 自动核对运费 → 重算精准利润）" style={{ marginBottom: 16 }}>
        <Space wrap style={{ marginBottom: 12 }}>
          <Upload accept=".xlsx,.xls" showUploadList={false} beforeUpload={parseBill}>
            <Button icon={<UploadOutlined />} type="primary" disabled={!!preview}>上传快递账单</Button>
          </Upload>
          {preview && <Button icon={<ReloadOutlined />} onClick={() => setPreview(null)}>清除预览</Button>}
          <span style={{ color: '#8c8c8c', fontSize: 12 }}>支持极兔等快递 Excel 账单（自动识别运单编号/总运费/区域列）；转退件按正常件对账；区域不一致会标注；对账后可单笔撤销</span>
        </Space>

        {preview && (
          <div>
            <Alert type={preview.mismatch_count ? 'warning' : 'success'} showIcon style={{ marginBottom: 12 }}
              message={`解析完成：账单 ${preview.bill_count} 单 / 总运费 ¥${yuan(preview.total_bill_fee)}`}
              description={
                <Space wrap>
                  <Tag color="#3F8600">匹配 {preview.matched_count} 单</Tag>
                  <Tag color="#CF1322">未匹配 {preview.unmatched_count} 单</Tag>
                  <Tag color="#EF9F27">已对账跳过 {preview.skipped_count} 单</Tag>
                  {preview.mismatch_count > 0 && <Tag color="#CF1322">区域不一致 {preview.mismatch_count} 单</Tag>}
                  <span>原运费合计 ¥{yuan(preview.total_old_fee)} → 账单合计 ¥{yuan(preview.total_new_fee)}（差额 <b style={{ color: preview.diff > 0 ? '#CF1322' : '#3F8600' }}>{preview.diff > 0 ? '+' : ''}{yuan(preview.diff)}</b>）</span>
                </Space>
              } />
            <Table rowKey="platform_order_no" size="small" columns={matchedColumns} dataSource={preview.matched} scroll={{ x: 1100 }} pagination={{ pageSize: 10 }} style={{ marginBottom: 12 }} />
            {preview.unmatched.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <Tag color="#CF1322">未匹配 {preview.unmatched.length} 单（系统无此单号，不影响对账）</Tag>
                <span style={{ color: '#8c8c8c', fontSize: 12 }}>例如：{preview.unmatched.slice(0, 5).map(u => u.tracking_no).join('、')}…；点「导出差异明细」可拉出全部未匹配/不一致清单</span>
              </div>
            )}
            <Space wrap style={{ marginBottom: 4 }}>
              {appliedSummary ? (
                <>
                  <Tag color="#3F8600" style={{ marginRight: 0 }}>运费已更新并重算利润</Tag>
                  <Button type="primary" icon={<SaveOutlined />} onClick={() => setSaveModal(true)}>保存本月对账记录</Button>
                  <Dropdown menu={{ items: EXPORT_TYPES.map(t => ({ key: t.key, label: t.label })), onClick: ({ key }) => exportDiff(key) }}>
                    <Button icon={<FileExcelOutlined />}>导出差异明细<DownOutlined /></Button>
                  </Dropdown>
                  <Button icon={<ReloadOutlined />} onClick={() => { setAppliedSummary(null); setPreview(null); }}>完成</Button>
                </>
              ) : (
                <>
                  <Popconfirm title={`确认应用对账？将更新 ${preview.matched_count} 笔订单运费并重算利润`} onConfirm={applyBill}>
                    <Button type="primary" icon={<CheckOutlined />} loading={applying}>确认对账（更新运费 + 重算利润）</Button>
                  </Popconfirm>
                  {preview.mismatch_count > 0 && (
                    <Button icon={<EditOutlined />} onClick={openRegionModal}>修正区域（{preview.mismatch_count} 单）</Button>
                  )}
                  <Dropdown menu={{ items: EXPORT_TYPES.map(t => ({ key: t.key, label: t.label })), onClick: ({ key }) => exportDiff(key) }}>
                    <Button icon={<FileExcelOutlined />}>导出差异明细<DownOutlined /></Button>
                  </Dropdown>
                </>
              )}
            </Space>
          </div>
        )}
      </Card>

      <Card title={`对账历史（${history.length}）`} extra={<Button icon={<ReloadOutlined />} onClick={loadHistory}>刷新</Button>}>
        <Table rowKey="id" size="small" dataSource={history} pagination={{ pageSize: 8 }} scroll={{ x: 1100 }}
          columns={[
            { title: '编号', dataIndex: 'id', width: 50 },
            { title: '快递公司', dataIndex: 'company', width: 100 },
            { title: '账单日期', dataIndex: 'bill_date', width: 110 },
            { title: '账单单数', dataIndex: 'bill_count', width: 80, align: 'right' },
            { title: '匹配更新', dataIndex: 'applied', width: 80, align: 'right', render: (v, r) => <b>{v}</b> },
            { title: '未匹配', dataIndex: 'unmatched_count', width: 70, align: 'right' },
            { title: '原运费合计', dataIndex: 'total_old_fee', width: 100, align: 'right', render: v => yuan(v) },
            { title: '新运费合计', dataIndex: 'total_new_fee', width: 100, align: 'right', render: v => <b style={{ color: 'var(--brand)' }}>{yuan(v)}</b> },
            { title: '差额', dataIndex: 'diff', width: 100, align: 'right',
              render: v => <span style={{ color: v > 0 ? '#CF1322' : v < 0 ? '#3F8600' : '#999', fontWeight: 600 }}>{v > 0 ? '+' : ''}{yuan(v)}</span> },
            { title: '区域不一致', dataIndex: 'mismatch_count', width: 90, align: 'center', render: v => v ? <Tag color="#CF1322">{v}</Tag> : '—' },
            { title: '撤销', key: 'reverted', width: 80, align: 'center', render: (_, r) => r.reverted_count ? <Tag color="#EF9F27">已撤{r.reverted_count}</Tag> : '—' },
            { title: '时间', dataIndex: 'created_at', width: 150 },
            { title: '明细', width: 80, align: 'center',
              render: (_, r) => <Button size="small" onClick={() => setHistoryModal(r)}>查看</Button> },
          ]} />
      </Card>

      <Card title={`月度对账记录（${monthlySummary.length} 个月）`} extra={<Button icon={<ReloadOutlined />} onClick={loadMonthly}>刷新</Button>} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: '#8C8178', marginBottom: 8 }}>按月汇总 = 当月<b>全部对账执行记录自动合并</b>（含同月多次对账；已撤销单自动剔除差额贡献），不漏任何一笔</div>
        <Table rowKey="month" size="small" dataSource={monthlySummary} pagination={false} style={{ marginBottom: 16 }}
          columns={[
            { title: '对账月份', dataIndex: 'month', width: 100, render: v => <b style={{ color: 'var(--brand)' }}>{v}</b> },
            { title: '对账笔数', dataIndex: 'runs', width: 85, align: 'right', render: v => <Tag>{v} 笔</Tag> },
            { title: '账单单数', dataIndex: 'bill_count', width: 85, align: 'right' },
            { title: '匹配更新', dataIndex: 'matched_count', width: 85, align: 'right', render: v => <b>{v}</b> },
            { title: '未匹配', dataIndex: 'unmatched_count', width: 70, align: 'right' },
            { title: '账单运费合计', dataIndex: 'total_new_fee', width: 110, align: 'right', render: v => yuan(v) },
            { title: '差额', dataIndex: 'diff', width: 100, align: 'right',
              render: v => <span style={{ color: v > 0 ? '#CF1322' : v < 0 ? '#3F8600' : '#999', fontWeight: 600 }}>{v > 0 ? '+' : ''}{yuan(v)}</span> },
            { title: '区域不一致', dataIndex: 'mismatch_count', width: 90, align: 'center', render: v => v ? <Tag color="#CF1322">{v}</Tag> : '—' },
          ]} />
        <div style={{ fontWeight: 600, marginBottom: 6 }}>归档留底（手动保存）</div>
        <Table rowKey="id" size="small" dataSource={monthly} pagination={{ pageSize: 8 }} scroll={{ x: 1000 }}
          columns={[
            { title: '对账月份', dataIndex: 'month', width: 100, render: v => <b style={{ color: 'var(--brand)' }}>{v}</b> },
            { title: '账单单数', dataIndex: 'bill_count', width: 85, align: 'right' },
            { title: '匹配更新', dataIndex: 'matched_count', width: 85, align: 'right', render: v => <b>{v}</b> },
            { title: '未匹配', dataIndex: 'unmatched_count', width: 70, align: 'right' },
            { title: '账单运费合计', dataIndex: 'total_new_fee', width: 110, align: 'right', render: v => yuan(v) },
            { title: '差额', dataIndex: 'diff', width: 100, align: 'right',
              render: v => <span style={{ color: v > 0 ? '#CF1322' : v < 0 ? '#3F8600' : '#999', fontWeight: 600 }}>{v > 0 ? '+' : ''}{yuan(v)}</span> },
            { title: '区域不一致', dataIndex: 'mismatch_count', width: 90, align: 'center', render: v => v ? <Tag color="#CF1322">{v}</Tag> : '—' },
            { title: '备注', dataIndex: 'remark', ellipsis: true, render: v => v || <span style={{ color: '#bfbfbf' }}>—</span> },
            { title: '保存时间', dataIndex: 'created_at', width: 150 },
            { title: '操作', width: 70, align: 'center',
              render: (_, r) => <Popconfirm title="删除这条月度记录？（仅删归档，不影响订单运费）" onConfirm={() => deleteMonthly(r.id)}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm> },
          ]} />
      </Card>

      <Modal title={`对账明细 #${historyModal ? historyModal.id : ''}（${historyModal ? historyModal.company : ''}）`} open={!!historyModal} footer={null} width={900} onCancel={() => setHistoryModal(null)}>
        {historyModal && (
          <Table rowKey={(r) => r.platform_order_no || r.order_id} size="small" dataSource={historyModal.items} pagination={{ pageSize: 10 }} scroll={{ x: 800 }}
            columns={[
              { title: '订单编号', dataIndex: 'order_id', width: 70 },
              { title: '快递单号', dataIndex: 'tracking_no', width: 150 },
              { title: '原运费', dataIndex: 'old_fee', width: 90, align: 'right', render: v => yuan(v) },
              { title: '新运费', dataIndex: 'new_fee', width: 90, align: 'right', render: v => <b style={{ color: 'var(--brand)' }}>{yuan(v)}</b> },
              { title: '区域不一致', dataIndex: 'region_mismatch', width: 100, align: 'center', render: v => v ? <Tag color="#CF1322">是</Tag> : '—' },
              { title: '操作', width: 90, align: 'center',
                render: (_, it) => (historyModal.reverted || []).includes(it.order_id)
                  ? <Tag color="#EF9F27">已撤销</Tag>
                  : <Popconfirm title="撤销该订单对账？恢复原运费并重算利润" onConfirm={() => revert(historyModal.id, it.order_id)}><Button size="small" icon={<UndoOutlined />}>撤销</Button></Popconfirm> },
            ]} />
        )}
      </Modal>

      <Modal title="保存月度对账记录" open={saveModal} onOk={saveRecord} confirmLoading={saving} onCancel={() => setSaveModal(false)} okText="保存">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ marginBottom: 6, color: '#666' }}>对账月份</div>
            <DatePicker picker="month" value={saveMonth ? dayjs(saveMonth) : null} onChange={(d) => setSaveMonth(d ? d.format('YYYY-MM') : '')} style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ marginBottom: 6, color: '#666' }}>备注（可选）</div>
            <Input.TextArea rows={3} placeholder="例如：9月极兔账单已对账，差额已核对确认" value={saveRemark} onChange={e => setSaveRemark(e.target.value)} />
          </div>
          {appliedSummary && (
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              将归档：账单 {appliedSummary.summary.bill_count} 单 / 匹配 {appliedSummary.summary.matched_count} 单 / 未匹配 {appliedSummary.summary.unmatched_count} 单 / 账单运费合计 ¥{yuan(appliedSummary.summary.total_new_fee)}
            </div>
          )}
        </div>
      </Modal>

      <Modal title="修正区域不一致" open={regionModal} width={780} footer={null} onCancel={() => setRegionModal(false)}>
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="勾选要修正的订单，确认后把系统区域改为账单区域（可自动映射的默认已勾选；「五区/六区」等无法映射的需手动与快递核对）" />
        <Table rowKey="platform_order_no" size="small"
          dataSource={(preview?.matched || []).filter(m => m.region_mismatch)}
          pagination={{ pageSize: 10 }} scroll={{ x: 700 }}
          rowSelection={{ selectedRowKeys: regionSel, onChange: (keys) => setRegionSel(keys), getCheckboxProps: (r) => ({ disabled: !r.mappable }) }}
          columns={[
            { title: '快递单号', dataIndex: 'tracking_no', width: 150 },
            { title: '店铺', dataIndex: 'shop', width: 120, ellipsis: true },
            { title: '账单区域', dataIndex: 'bill_region', width: 90, align: 'center', render: v => <b style={{ color: 'var(--brand)' }}>{v}</b> },
            { title: '系统区域', dataIndex: 'sys_region', width: 90, align: 'center' },
            { title: '修正为', key: 'target', width: 100, align: 'center',
              render: (_, r) => r.mappable ? <Tag color="#3F8600">{r.target_zone}</Tag> : <Tag color="#CF1322">需手动处理</Tag> },
          ]} />
        <div style={{ marginTop: 12, textAlign: 'right' }}>
          <Space>
            <Button onClick={() => setRegionModal(false)}>取消</Button>
            <Button type="primary" icon={<CheckOutlined />} loading={regionUpdating} onClick={updateRegions}>确认修改区域（{regionSel.length} 单）</Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
}
